import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plane, MapPin, ArrowRight, ArrowUpDown, Mic, Loader2, Square, X } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import AddressAutocomplete from "./AddressAutocomplete";
import { DateField, TimeField } from "./PickerField";

const AIRPORT = "Flughafen Zürich (ZRH)";
const isAirport = (s) => /flughafen\s*z|zrh|z[uü]rich\s*airport|airport\s*z[uü]rich|kloten/i.test(s || "");

const LAB = {
  de: { heading: "Fixpreis berechnen & buchen", voice: "Mit Sprache buchen", listening: "Ich höre zu … tippen zum Stoppen", processing: "Sprache wird verarbeitet …", calc: "Preis berechnen", pickupPh: "Abholadresse", destPh: "Zielort eingeben", micErr: "Mikrofon-Zugriff verweigert.", voiceErr: "Sprache konnte nicht verarbeitet werden.", voiceNone: "Nichts erkannt – bitte erneut versuchen.", voiceOk: "Verstanden", addr: "Bitte geben Sie eine Zieladresse ein." },
  en: { heading: "Calculate fixed price & book", voice: "Book by voice", listening: "Listening … tap to stop", processing: "Processing voice …", calc: "Calculate price", pickupPh: "Pickup address", destPh: "Enter destination", micErr: "Microphone access denied.", voiceErr: "Could not process voice.", voiceNone: "Nothing recognised – please try again.", voiceOk: "Understood", addr: "Please enter a destination address." },
  fr: { heading: "Calculer le prix fixe & réserver", voice: "Réserver à la voix", listening: "J'écoute … appuyez pour arrêter", processing: "Traitement de la voix …", calc: "Calculer le prix", pickupPh: "Adresse de prise en charge", destPh: "Saisir la destination", micErr: "Accès au micro refusé.", voiceErr: "Impossible de traiter la voix.", voiceNone: "Rien reconnu – réessayez.", voiceOk: "Compris", addr: "Veuillez saisir une adresse de destination." },
  it: { heading: "Calcola il prezzo fisso & prenota", voice: "Prenota a voce", listening: "In ascolto … tocca per fermare", processing: "Elaborazione voce …", calc: "Calcola il prezzo", pickupPh: "Indirizzo di ritiro", destPh: "Inserisci la destinazione", micErr: "Accesso al microfono negato.", voiceErr: "Impossibile elaborare la voce.", voiceNone: "Niente riconosciuto – riprova.", voiceOk: "Capito", addr: "Inserisci un indirizzo di destinazione." },
};

// Compact, reusable booking form for blog/content/SEO pages.
const BookingWidget = ({ defaultTo = "", heading, className = "" }) => {
  const { lang: rawLang } = useLang();
  const lang = ["de", "en", "fr", "it"].includes(rawLang) ? rawLang : "de";
  const lab = LAB[lang];
  const navigate = useNavigate();
  const API = process.env.REACT_APP_BACKEND_URL;

  const [pickup, setPickup] = useState(AIRPORT);
  const [dropoff, setDropoff] = useState(defaultTo);
  const today = new Date().toISOString().split("T")[0];
  const _now1h = new Date(Date.now() + 60 * 60 * 1000);
  _now1h.setMinutes(Math.round(_now1h.getMinutes() / 5) * 5, 0, 0);
  const _pad = (n) => String(n).padStart(2, "0");
  const [date, setDate] = useState(`${_now1h.getFullYear()}-${_pad(_now1h.getMonth() + 1)}-${_pad(_now1h.getDate())}`);
  const [time, setTime] = useState(`${_pad(_now1h.getHours())}:${_pad(_now1h.getMinutes())}`);

  const [recording, setRecording] = useState(false);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const voiceSupported = typeof window !== "undefined" && !!(navigator.mediaDevices && window.MediaRecorder);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const swap = () => { setPickup(dropoff); setDropoff(pickup); };

  const sendVoice = async (blob) => {
    setVoiceBusy(true);
    try {
      const fd = new FormData();
      fd.append("audio", blob, "voice.webm");
      const res = await fetch(`${API}/api/voice/parse`, { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error("fail");
      if (data.destination) {
        if (data.direction === "to_airport") { setPickup(data.destination); setDropoff(AIRPORT); }
        else { setPickup(AIRPORT); setDropoff(data.destination); }
      }
      if (data.date) setDate(data.date);
      if (data.time) setTime(data.time);
      if (data.transcript) toast.success(`${lab.voiceOk}: „${data.transcript}"`);
      else toast.error(lab.voiceNone);
    } catch (e) {
      toast.error(lab.voiceErr);
    } finally {
      setVoiceBusy(false);
    }
  };

  const toggleVoice = async () => {
    if (recording) { mediaRef.current && mediaRef.current.stop(); setRecording(false); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((tr) => tr.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size > 0) await sendVoice(blob);
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      toast.error(lab.micErr);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!pickup.trim() || !dropoff.trim()) { toast.error(lab.addr); return; }
    const params = new URLSearchParams({ from: pickup, to: dropoff, date, time, tab: "oneway" });
    navigate(`/booking?${params.toString()}`);
  };

  return (
    <div className={`bg-white rounded-3xl border border-[#E9E5DE] p-4 sm:p-6 elev-lg ${className}`} data-testid="booking-widget">
      <h2 className="font-heading font-bold text-lg sm:text-xl text-[#0B1533] mb-4 flex items-center gap-2">
        <Plane size={18} className="text-[#2B4BFF]" /> {heading || lab.heading}
      </h2>
      {voiceSupported && (
        <button type="button" onClick={toggleVoice} disabled={voiceBusy} data-testid="bw-voice-btn" className={`w-full flex items-center justify-center gap-2.5 rounded-2xl py-3 mb-3 font-body font-semibold text-sm transition-all duration-200 ${recording ? "bg-[#C0392B] text-white animate-pulse" : "bg-[#0B1533] text-white hover:bg-[#1a2647]"} disabled:opacity-60`}>
          {voiceBusy ? (<><Loader2 size={18} className="animate-spin" /> {lab.processing}</>) : recording ? (<><Square size={16} className="fill-white" /> {lab.listening}</>) : (<><Mic size={18} className="text-[#C9A24B]" /> {lab.voice}</>)}
        </button>
      )}
      <form onSubmit={submit} className="space-y-3">
        <div className="relative">
          <AddressAutocomplete value={pickup} onChange={setPickup} onSelect={(p) => setPickup(p.label)} placeholder={lab.pickupPh} icon={Plane} className="w-full bg-white border border-[#E9E5DE] rounded-2xl pl-11 pr-20 py-3 font-body text-sm text-[#0B1533] focus:border-[#2B4BFF] focus:ring-2 focus:ring-[#2B4BFF]/15 focus:outline-none transition-all placeholder:text-[#9CA0B0]" />
          {pickup && <button type="button" onClick={() => setPickup("")} aria-label="Clear" data-testid="bw-pickup-clear" className="absolute right-[52px] top-1/2 -translate-y-1/2 z-20 w-7 h-7 rounded-full bg-[#F5F2ED] text-[#5A607A] flex items-center justify-center hover:bg-[#E9E5DE] transition-colors"><X size={14} /></button>}
          <button type="button" onClick={swap} aria-label="Swap" data-testid="bw-swap-btn" className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-xl bg-[#0B1533] text-white flex items-center justify-center hover:bg-[#2B4BFF] transition-colors">
            <ArrowUpDown size={16} />
          </button>
        </div>
        <AddressAutocomplete value={dropoff} onChange={setDropoff} onSelect={(p) => setDropoff(p.label)} placeholder={lab.destPh} icon={MapPin} className="w-full bg-white border border-[#E9E5DE] rounded-2xl pl-11 pr-3 py-3 font-body text-sm text-[#0B1533] focus:border-[#2B4BFF] focus:ring-2 focus:ring-[#2B4BFF]/15 focus:outline-none transition-all placeholder:text-[#9CA0B0]" />
        <div className="grid grid-cols-2 gap-3">
          <div className="min-w-0"><DateField value={date} min={today} onChange={setDate} testid="bw-date-field" /></div>
          <div className="min-w-0"><TimeField value={time} onChange={setTime} testid="bw-time-field" /></div>
        </div>
        <button type="submit" data-testid="bw-submit-btn" className="group w-full flex items-center justify-center gap-2 bg-[#2B4BFF] text-white font-body font-semibold text-base rounded-2xl py-3.5 hover:bg-[#1E38D8] transition-all duration-200 elev-soft">
          {lab.calc}
          <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-200" />
        </button>
      </form>
    </div>
  );
};

export default BookingWidget;
